import _ from 'lodash';
import classNames from "classnames";
import {useTranslation} from "react-i18next";
import {useEffect, useMemo, useState} from "react";
import {Link} from "react-router-dom";
import ScrollReveal from "../components/ScrollReveal";
import SocialIcons from "../components/SocialIcons";
import BackToTop from "../components/BackToTop";
import useFetchNews from "../hooks/useNews";
import AboutPage from "./About";
import PlatformPage from "./Platform";
import ComponentsPage from "./Components";
import AppsPage from "./Apps";
import RoadmapPage from "./Roadmap";
import DocumentsPage from "./Documents";

import logo from '../assets/logos/lavita@2x.png';
import faces from '../assets/backgrounds/graphic_hp@2x.png';
import appStore from '../assets/logos/app-store.png';
import googlePlay from '../assets/logos/google-play.png';

const TITLE_ANIMATIONS = {
    hero: 'titleHeroReveal 1s cubic-bezier(0.22, 1, 0.36, 1) forwards',
    story: 'titleStoryReveal 0.8s cubic-bezier(0.22, 1, 0.36, 1) forwards',
    'story-right': 'titleStoryRevealRight 0.8s cubic-bezier(0.22, 1, 0.36, 1) forwards',
    info: 'titleInfoReveal 0.78s cubic-bezier(0.22, 1, 0.36, 1) forwards',
    cta: 'titleCtaReveal 0.9s cubic-bezier(0.22, 1, 0.36, 1) forwards',
    action: 'titleActionReveal 0.82s cubic-bezier(0.22, 1, 0.36, 1) forwards'
};

const HOME_NEWS_COUNT = 3;

const HomePage = () => {
    const {t, i18n} = useTranslation();
    const {news} = useFetchNews(i18n.language);
    const [introComplete, setIntroComplete] = useState(false);

    useEffect(() => {
        document.body.classList.add('Body--home');

        const introTimer = window.setTimeout(() => {
            setIntroComplete(true);
        }, 1350);

        return () => {
            document.body.classList.remove('Body--home');
            window.clearTimeout(introTimer);
        };
    }, []);

    const heroStats = useMemo(() => ([
        t('home.stats.privacy'),
        t('home.stats.intelligence'),
        t('home.stats.control')
    ]), [t]);

    const scenes = useMemo(() => ([
        {
            slug: 'ai',
            anchor: 'intelligence',
            layout: 'split',
            motion: 'story',
            eyebrow: t('home.scenes.ai.eyebrow'),
            title: t('home.scenes.ai.title'),
            description: t('home.scenes.ai.description'),
            bullets: [
                t('home.scenes.ai.bullet_1'),
                t('home.scenes.ai.bullet_2'),
                t('home.scenes.ai.bullet_3')
            ]
        },
        {
            slug: 'privacy',
            anchor: 'privacy-infrastructure',
            layout: 'stacked',
            motion: 'story-right',
            eyebrow: t('home.scenes.privacy.eyebrow'),
            title: t('home.scenes.privacy.title'),
            description: t('home.scenes.privacy.description'),
            bullets: [
                t('home.scenes.privacy.bullet_1'),
                t('home.scenes.privacy.bullet_2'),
                t('home.scenes.privacy.bullet_3')
            ]
        },
        {
            slug: 'ecosystem',
            anchor: 'connected-experience',
            layout: 'offset',
            motion: 'story',
            eyebrow: t('home.scenes.ecosystem.eyebrow'),
            title: t('home.scenes.ecosystem.title'),
            description: t('home.scenes.ecosystem.description'),
            bullets: [
                t('home.scenes.ecosystem.bullet_1'),
                t('home.scenes.ecosystem.bullet_2'),
                t('home.scenes.ecosystem.bullet_3')
            ]
        }
    ]), [t]);

    const ecosystemCards = useMemo(() => ([
        {
            title: t('home.ecosystem.mobile_title'),
            description: t('home.ecosystem.mobile_description')
        },
        {
            title: t('home.ecosystem.web_title'),
            description: t('home.ecosystem.web_description')
        },
        {
            title: t('home.ecosystem.marketplace_title'),
            description: t('home.ecosystem.marketplace_description')
        }
    ]), [t]);

    const visibleNews = useMemo(() => (
        _.take(news, HOME_NEWS_COUNT)
    ), [news]);

    const renderHero = (sectionClassName = '') => (
        <section id={'home'}
                 className={classNames('HomeHero', 'HomeSectionAnimTarget', sectionClassName)}
        >
            <div className={'HomeHero__backdrop'}>
                <div className={'HomeHero__grid'}/>
                <div className={'HomeHero__orb HomeHero__orb--left'}/>
                <div className={'HomeHero__orb HomeHero__orb--right'}/>
                <div className={'HomeHero__beam'}/>
            </div>

            <div className={'HomeHero__content'}>
                <div className={'HomeHero__copy'}>
                    <ScrollReveal as={'div'} className={'HomeHero__eyebrow'} threshold={0.1} variant={'left'}>
                        {t('home.hero_eyebrow')}
                    </ScrollReveal>
                    <ScrollReveal as={'h1'} className={'HomeHero__title'} threshold={0.1} delay={120} variant={'up'}>
                        <span className={'HomeTitleMotion HomeTitleMotion--hero'}
                              data-motion={'hero'}>
                            {t('home.hero_title')}
                        </span>
                    </ScrollReveal>
                    <ScrollReveal as={'p'} className={'HomeHero__description'} threshold={0.1} delay={220} variant={'right'}>
                        {t('home.hero_description')}
                    </ScrollReveal>

                    <ScrollReveal as={'div'} className={'HomeHero__stats'} threshold={0.1} delay={280} variant={'up'}>
                        {
                            _.map(heroStats, (item, index) => (
                                <div key={index} className={'HomeHero__stat'}>
                                    {item}
                                </div>
                            ))
                        }
                    </ScrollReveal>
                </div>

                <ScrollReveal as={'div'} className={'HomeHero__visual'} threshold={0.1} delay={180} variant={'right'}>
                    <div className={'HomeHero__visual-shell'}>
                        <div className={'HomeHero__visual-frame'}>
                            <img src={faces}
                                 className={'HomeHero__faces'}
                                 alt={''}
                            />
                        </div>
                    </div>
                </ScrollReveal>
            </div>
        </section>
    );

    const renderStoryPanel = (scene, index, sectionClassName = '') => (
        <section id={scene.anchor}
                 key={scene.slug}
                 className={classNames('HomeStoryPanel', 'HomeSectionAnimTarget', `HomeStoryPanel--${scene.slug}`, `HomeStoryPanel--${scene.layout}`, sectionClassName)}
        >
            <div className={'HomeStoryPanel__layout'}>
                <ScrollReveal as={'div'}
                              className={'HomeStoryPanel__copy'}
                              threshold={0.12}
                              variant={scene.slug === 'privacy' ? 'right' : 'left'}
                >
                    <div className={'HomeStoryPanel__eyebrow'}>
                        {scene.eyebrow}
                    </div>
                    <h2>
                        <span className={'HomeTitleMotion HomeTitleMotion--story'}
                              data-motion={scene.motion}>
                            {scene.title}
                        </span>
                    </h2>
                    <p>{scene.description}</p>
                    <div className={'HomeStoryPanel__bullets'}>
                        {
                            _.map(scene.bullets, (item, bulletIndex) => (
                                <div key={bulletIndex} className={'HomeStoryPanel__bullet'}>
                                    {item}
                                </div>
                            ))
                        }
                    </div>
                </ScrollReveal>

                <ScrollReveal as={'div'}
                              className={'HomeStoryPanel__visual'}
                              threshold={0.12}
                              delay={120}
                              variant={scene.slug === 'privacy' ? 'tilt-left' : 'tilt-right'}
                >
                    {
                        scene.slug === 'ai' &&
                        <div className={'HomeStoryPanel__frame HomeStoryPanel__frame--flow'}>
                            <div className={'HomeStoryPanel__grid'}/>
                            <div className={'HomeStoryPanel__flow-path HomeStoryPanel__flow-path--1'}/>
                            <div className={'HomeStoryPanel__flow-path HomeStoryPanel__flow-path--2'}/>
                            <div className={'HomeStoryPanel__flow-path HomeStoryPanel__flow-path--3'}/>
                            <div className={'HomeStoryPanel__flow-node HomeStoryPanel__flow-node--a'}/>
                            <div className={'HomeStoryPanel__flow-node HomeStoryPanel__flow-node--b'}/>
                            <div className={'HomeStoryPanel__flow-node HomeStoryPanel__flow-node--c'}/>
                            <div className={'HomeStoryPanel__micro-label HomeStoryPanel__micro-label--top'}>
                                <span>{scene.eyebrow}</span>
                                <strong>{scene.bullets[0]}</strong>
                            </div>
                            <div className={'HomeStoryPanel__micro-label HomeStoryPanel__micro-label--bottom'}>
                                <span>{t('home.story_visual.label_status')}</span>
                                <strong>{scene.bullets[1]}</strong>
                            </div>
                        </div>
                    }

                    {
                        scene.slug === 'privacy' &&
                        <div className={'HomeStoryPanel__frame HomeStoryPanel__frame--shield'}>
                            <div className={'HomeStoryPanel__shield-ring HomeStoryPanel__shield-ring--outer'}/>
                            <div className={'HomeStoryPanel__shield-ring HomeStoryPanel__shield-ring--middle'}/>
                            <div className={'HomeStoryPanel__shield-ring HomeStoryPanel__shield-ring--inner'}/>
                            <div className={'HomeStoryPanel__shield-core'}>
                                <div className={'HomeStoryPanel__shield-lock'}/>
                            </div>
                            <div className={'HomeStoryPanel__shield-orbit HomeStoryPanel__shield-orbit--left'}>
                                <div className={'HomeStoryPanel__shield-slice HomeStoryPanel__shield-slice--left'}>
                                    <span>{t('home.story_visual.label_primary')}</span>
                                    <strong>{scene.bullets[0]}</strong>
                                </div>
                            </div>
                            <div className={'HomeStoryPanel__shield-orbit HomeStoryPanel__shield-orbit--right'}>
                                <div className={'HomeStoryPanel__shield-slice HomeStoryPanel__shield-slice--right'}>
                                    <span>{t('home.story_visual.label_secondary')}</span>
                                    <strong>{scene.bullets[1]}</strong>
                                </div>
                            </div>
                            <div className={'HomeStoryPanel__shield-orbit HomeStoryPanel__shield-orbit--bottom'}>
                                <div className={'HomeStoryPanel__shield-slice HomeStoryPanel__shield-slice--bottom'}>
                                    <span>{t('home.story_visual.label_status')}</span>
                                    <strong>{scene.bullets[2]}</strong>
                                </div>
                            </div>
                        </div>
                    }

                    {
                        scene.slug === 'ecosystem' &&
                        <div className={'HomeStoryPanel__frame HomeStoryPanel__frame--network'}>
                            <div className={'HomeStoryPanel__network-link HomeStoryPanel__network-link--1'}/>
                            <div className={'HomeStoryPanel__network-link HomeStoryPanel__network-link--2'}/>
                            <div className={'HomeStoryPanel__network-link HomeStoryPanel__network-link--3'}/>
                            <div className={'HomeStoryPanel__network-card HomeStoryPanel__network-card--a'}>
                                <span>{scene.eyebrow}</span>
                                <strong>{scene.bullets[0]}</strong>
                            </div>
                            <div className={'HomeStoryPanel__network-card HomeStoryPanel__network-card--b'}>
                                <span>{t('home.story_visual.label_primary')}</span>
                                <strong>{scene.bullets[1]}</strong>
                            </div>
                            <div className={'HomeStoryPanel__network-card HomeStoryPanel__network-card--c'}>
                                <span>{t('home.story_visual.label_secondary')}</span>
                                <strong>{scene.bullets[2]}</strong>
                            </div>
                            <div className={'HomeStoryPanel__network-hub'}>
                                <span>{t('home.story_visual.label_status')}</span>
                            </div>
                        </div>
                    }
                </ScrollReveal>
            </div>
        </section>
    );

    const renderEcosystem = (sectionClassName = '') => (
        <section id={'ecosystem'}
                 className={classNames('HomeEcosystem', 'HomeSectionAnimTarget', sectionClassName)}>
            <ScrollReveal as={'div'} className={'HomeEcosystem__heading'} threshold={0.12} variant={'right'}>
                <div className={'HomeEcosystem__eyebrow'}>
                    {t('home.ecosystem.eyebrow')}
                </div>
                <h2>
                    <span className={'HomeTitleMotion HomeTitleMotion--info'}
                          data-motion={'info'}>
                        {t('home.ecosystem.title')}
                    </span>
                </h2>
                <p>{t('home.ecosystem.description')}</p>
            </ScrollReveal>

            <div className={'HomeEcosystem__stack'}>
                {
                    _.map(ecosystemCards, (item, index) => (
                        <ScrollReveal key={item.title}
                                      className={classNames('HomeEcosystem__band', `HomeEcosystem__band--${index + 1}`)}
                                      threshold={0.12}
                                      delay={120 + index * 90}
                                      variant={index === 1 ? 'right' : 'left'}
                        >
                            <div className={'HomeEcosystem__band-index'}>
                                0{index + 1}
                            </div>
                            <div className={'HomeEcosystem__band-body'}>
                                <h3>{item.title}</h3>
                                <p>{item.description}</p>
                            </div>
                        </ScrollReveal>
                    ))
                }
            </div>
        </section>
    );

    const renderNews = (sectionClassName = '') => {
        return (
            <section id={'news'}
                     className={classNames('HomeNews', 'HomeSectionAnimTarget', sectionClassName)}
            >
                <ScrollReveal as={'div'} className={'HomeNews__heading'} threshold={0.12} variant={'left'}>
                    <div className={'HomeNews__eyebrow'}>
                        {t('home.recent_news')}
                    </div>
                    <h2>
                        <span className={'HomeTitleMotion HomeTitleMotion--info'}
                              data-motion={'info'}>
                            {t('home.news_title')}
                        </span>
                    </h2>
                </ScrollReveal>

                <div className={'HomeNews__grid'}>
                    {
                        _.map(visibleNews, (item, index) => (
                            <ScrollReveal key={index}
                                          as={'a'}
                                          className={'HomeNews__card'}
                                          href={item.url}
                                          target={'_blank'}
                                          rel={'noreferrer'}
                                          threshold={0.12}
                                          delay={120 + index * 90}
                                          variant={index % 2 === 0 ? 'up' : 'down'}
                            >
                                <div className={'HomeNews__meta'}>
                                    {`${item.date} / ${item.source}`}
                                </div>
                                <div className={'HomeNews__card-title'}>
                                    {item.title}
                                </div>
                                <div className={'HomeNews__link'}>
                                    {t('home.view_more')}
                                </div>
                            </ScrollReveal>
                        ))
                    }
                </div>

                <div className={'HomeNews__actions'}>
                    <Link
                        to={'/news'}
                        className={'HomeNews__button'}
                    >
                        {t('home.show_more_news')}
                    </Link>
                </div>
            </section>
        );
    };

    const renderActionPage = (sectionClassName = '') => (
        <section id={'download'}
                 className={classNames('HomeActionPage', 'HomeSectionAnimTarget', sectionClassName)}>
            <ScrollReveal as={'div'} className={'HomeActionPage__content'} threshold={0.12} variant={'up'}>
                <div className={'HomeActionPage__eyebrow'}>
                    {t('home.action_eyebrow')}
                </div>
                <h2>
                    <span className={'HomeTitleMotion HomeTitleMotion--action'}
                          data-motion={'action'}>
                        {t('home.action_title')}
                    </span>
                </h2>
                <p>{t('home.action_description')}</p>

                <div className={'HomeActionPage__social'}>
                    <span>{t('home.social_join')}</span>
                    <SocialIcons/>
                </div>

                <div className={'HomeActionPage__downloads'}>
                    <a href="https://play.google.com/store/apps/details?id=com.wuguidashi.lavitamobile" target="_blank" rel="noreferrer">
                        <img src={googlePlay} alt={'Download on Google Play'}/>
                    </a>
                    <a href="https://apps.apple.com/us/app/lavita-ai/id6745689821" target="_blank" rel="noreferrer">
                        <img src={appStore} alt={'Download on the App Store'}/>
                    </a>
                </div>

                <a href="https://platform.lavita.ai/login"
                   target="_blank"
                   rel="noreferrer"
                   className={'HomeActionPage__web-link'}
                >
                    {t('home.action_web_link')}
                </a>
            </ScrollReveal>
        </section>
    );

    const renderEmbeddedPage = (id, Component, className = '') => (
        <section id={id}
                 className={classNames('HomeEmbeddedSection', className)}
        >
            <Component embedded/>
        </section>
    );

    return (
        <div className={'Page HomePage'}>
            <div className={classNames('HomeIntro', {
                'HomeIntro--complete': introComplete
            })}>
                <div className={'HomeIntro__pulse'}/>
                <div className={'HomeIntro__ring HomeIntro__ring--outer'}/>
                <div className={'HomeIntro__ring HomeIntro__ring--inner'}/>
                <div className={'HomeIntro__logo-wrap'}>
                    <img src={logo}
                         className={'HomeIntro__logo'}
                         alt={'Lavita'}
                    />
                    <div className={'HomeIntro__caption'}>
                        {t('home.intro_caption')}
                    </div>
                </div>
            </div>
            {renderHero()}
            {_.map(scenes, renderStoryPanel)}
            {renderEcosystem()}
            {renderEmbeddedPage('about', AboutPage, 'HomeEmbeddedSection--about')}
            {renderEmbeddedPage('platform', PlatformPage, 'HomeEmbeddedSection--platform')}
            {renderEmbeddedPage('components', ComponentsPage, 'HomeEmbeddedSection--components')}
            {renderEmbeddedPage('apps', AppsPage, 'HomeEmbeddedSection--apps')}
            {renderEmbeddedPage('roadmap', RoadmapPage, 'HomeEmbeddedSection--roadmap')}
            {renderNews()}
            {renderEmbeddedPage('docs', DocumentsPage, 'HomeEmbeddedSection--docs')}
            {renderActionPage()}
            <BackToTop />
        </div>
    );
};

export default HomePage;
